// @ts-nocheck
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface PackageCardOption {
  id: string;
  label: string;
  description?: string;
  priceLabel: string;
  originalPriceLabel?: string;
  note?: string;
  family_plan?: boolean;
  type?: 'single' | 'monthly';
}

/**
 * Renders a reusable package card that displays a title, a list of features and selectable package options; emits selection changes.
 * @component
 * @selector app-shared-package-card
 * @example
 *   <app-shared-package-card
 *     [title]="'Pro Plan'"
 *     [features]="['Unlimited projects', 'Priority support']"
 *     [options]="packageOptions"
 *     [selectedOptionId]="currentSelection"
 *     (selectionChange)="onPackageSelected($event)">
 *   </app-shared-package-card>
 * @input string title - The header/title displayed for the package card.
 * @input string[] features - A list of feature strings to render (used with *ngFor).
 * @input PackageCardOption[] options - Array of option objects representing selectable packages (each typically includes an id and display data).
 * @input string | null selectedOptionId - The id of the currently selected option; null when no option is selected.
 * @output EventEmitter<string> selectionChange - Emits the selected option id whenever a user selects an option.
 *
 * Key methods:
 * - onSelect(optionId: string): Sets selectedOptionId to the provided id and emits selectionChange with that id.
 * - trackByFeature(_: number, feature: string): TrackBy function for features ngFor; returns the feature string to optimize rendering.
 * - trackByOption(_: number, option: PackageCardOption): TrackBy function for options ngFor; returns option.id to optimize rendering.
 * - getDescriptionParts(desc?: string): Parses a description string by splitting on '•' and returns an object { left, right } (or null if no description); useful for templating split descriptions.
 *
 * Lifecycle:
 * - This standalone component does not implement specific Angular lifecycle hooks (e.g., OnInit); it relies on input bindings for initialization and change detection.
 */
@Component({
  selector: 'app-shared-package-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './package-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-card.component.scss'],
})
export class SharedPackageCardComponent {
  @Input() title = '';
  @Input() features: string[] = [];
  @Input() options: PackageCardOption[] = [];
  @Input() selectedOptionId: string | null = null;
  @Output() selectionChange = new EventEmitter<string>();

  onSelect(optionId: string): void {
    this.selectedOptionId = optionId;
    this.selectionChange.emit(optionId);
  }

  trackByFeature(_: number, feature: string): string {
    return feature;
  }
  trackByOption(_: number, option: PackageCardOption): string {
    return option.id;
  }

  getDescriptionParts(desc?: string): { left: string; right: string } | null {
    if (!desc) return null;
    const parts = desc.split('•');
    if (parts.length < 2) {
      return { left: desc, right: '' };
    }
    return { left: parts[0].trim(), right: parts.slice(1).join('•').trim() };
  }
}
